import { USER_MANAGEMENT_MODE } from '@/consts/userManagement';
import {
  ORGANIZATION_BUNDLE_TO_API_FIELDS,
  getOrganizationFieldVisibilityForRole,
  getUserFieldVisibilityForRole,
  USER_BUNDLE_TO_API_FIELDS,
} from './policies';
import { getViewerRoles } from './roles';
import {
  IAuthRole,
  IOrganizationFieldBundle,
  IUserFieldBundle,
  IViewerContext,
  IVisibilityQualifier,
  ORGANIZATION_FIELD_BUNDLE,
  USER_FIELD_BUNDLE,
} from './types';
import {
  IGetUserByIdApiField,
  IGetUsersApiField,
  IOrganizationUserApiField,
  IUserListApiField,
} from '@/services/users/types';
import { IOrganizationApiField } from '@/services/organizations/types';

const VISIBILITY_RANK: Record<IVisibilityQualifier, number> = {
  hidden: 0,
  selfVisible: 1,
  scopedVisible: 2,
  visible: 3,
};

/** Picks the strongest visibility qualifier when the viewer has multiple roles. */
function mergeVisibilityQualifiers(qualifiers: IVisibilityQualifier[]): IVisibilityQualifier {
  return qualifiers.reduce(
    (highest, current) => (
      VISIBILITY_RANK[current] > VISIBILITY_RANK[highest] ? current : highest
    ),
    'hidden',
  );
}

/** Merges user field-bundle visibility across all viewer roles. */
function getMergedUserFieldVisibility(
  bundle: IUserFieldBundle,
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IVisibilityQualifier {
  const qualifiers = viewerRoles.map((role) => getUserFieldVisibilityForRole(bundle, role));
  return mergeVisibilityQualifiers(qualifiers);
}

/** Merges organization field-bundle visibility across all viewer roles. */
function getMergedOrganizationFieldVisibility(
  bundle: IOrganizationFieldBundle,
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IVisibilityQualifier {
  const qualifiers = viewerRoles.map((role) => getOrganizationFieldVisibilityForRole(bundle, role));
  return mergeVisibilityQualifiers(qualifiers);
}

/** Applies self/scoped context rules on top of a merged visibility qualifier. */
function isFieldVisibleForContext(
  visibility: IVisibilityQualifier,
  context: IViewerContext,
): boolean {
  if (visibility === 'hidden') return false;
  if (visibility === 'visible') return true;
  if (visibility === 'selfVisible') return context.isViewingOwnRecord === true;
  return true;
}

/** Returns user field bundles visible to the viewer for the given context. */
function getVisibleUserFieldBundles(
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IUserFieldBundle[] {
  return (Object.values(USER_FIELD_BUNDLE) as IUserFieldBundle[]).filter((bundle) => {
    const visibility = getMergedUserFieldVisibility(bundle, viewerRoles);
    return isFieldVisibleForContext(visibility, context);
  });
}

/** Returns organization field bundles visible to the viewer for the given context. */
function getVisibleOrganizationFieldBundles(
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IOrganizationFieldBundle[] {
  return (Object.values(ORGANIZATION_FIELD_BUNDLE) as IOrganizationFieldBundle[]).filter(
    (bundle) => {
      const visibility = getMergedOrganizationFieldVisibility(bundle, viewerRoles);
      return isFieldVisibleForContext(visibility, context);
    },
  );
}

/** Opt-in user-list fields not governed by field-bundle visibility. */
const OPT_IN_USER_LIST_API_FIELD = 'license' as const;
const OPT_IN_ORGANIZATION_USER_API_FIELDS = ['license', 'usageTimeLimit'] as const;
const OPT_IN_USER_BY_ID_API_FIELDS = ['organization', 'usageTimeLimit', 'platformRoles', 'license'] as const;

function getOptInFieldsForOrganizationUsers(
  context: IViewerContext,
): IOrganizationUserApiField[] {
  const mode = context.userManagementMode;
  if (
    mode === USER_MANAGEMENT_MODE.SELF_ORG_USERS
    || mode === USER_MANAGEMENT_MODE.ORG_SPECIFIC_USERS
  ) {
    return [...OPT_IN_ORGANIZATION_USER_API_FIELDS];
  }
  return [];
}

/** Adds API fields required by the current user-management mode (e.g. license list). */
function getModeSpecificUserFields(context: IViewerContext): IGetUsersApiField[] {
  if (context.userManagementMode === USER_MANAGEMENT_MODE.INDEPENDENT_USERS) {
    return [OPT_IN_USER_LIST_API_FIELD];
  }
  return [];
}

function getRequestableUserListFields(
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IUserListApiField[] {
  const visibleBundles = getVisibleUserFieldBundles(context, viewerRoles);
  const bundleFields = visibleBundles.flatMap((bundle) => USER_BUNDLE_TO_API_FIELDS[bundle]);

  return [...new Set(bundleFields)];
}

/** Returns user API field names the viewer is allowed
 *  to request for GET /organizations/{orgUuid}/users. */
export function getRequestableOrganizationUserFields(
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IOrganizationUserApiField[] {
  const bundleFields = getRequestableUserListFields(context, viewerRoles);
  const optInFields = getOptInFieldsForOrganizationUsers(context);

  return [...new Set([...bundleFields, ...optInFields])];
}

/** Returns user API field names the viewer is allowed to request for GET /users. */
export function getRequestableUserFields(
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IGetUsersApiField[] {
  const bundleFields = getRequestableUserListFields(context, viewerRoles);
  const modeFields = getModeSpecificUserFields(context);

  return [...new Set([...bundleFields, ...modeFields])];
}

/** Returns user API field names the viewer is allowed to request for GET /users/{userUuid}. */
export function getRequestableUserByIdFields(
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IGetUserByIdApiField[] {
  const bundleFields = getRequestableUserListFields(context, viewerRoles)
    .filter((field) => field !== 'deleted');

  return [...new Set([...bundleFields, ...OPT_IN_USER_BY_ID_API_FIELDS])];
}

/** Returns organization API field names the viewer is allowed to request for the given context. */
export function getRequestableOrganizationFields(
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): IOrganizationApiField[] {
  const visibleBundles = getVisibleOrganizationFieldBundles(context, viewerRoles);
  const bundleFields = visibleBundles.flatMap(
    (bundle) => ORGANIZATION_BUNDLE_TO_API_FIELDS[bundle],
  );

  return [...new Set(bundleFields)];
}

/** True when a single user field is included in the requestable set for this context. */
export function isUserFieldVisible(
  fieldName: IGetUsersApiField,
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): boolean {
  return getRequestableUserFields(context, viewerRoles).includes(fieldName);
}

/** True when a single organization field is included in the requestable set for this context. */
export function isOrganizationFieldVisible(
  fieldName: IOrganizationApiField,
  context: IViewerContext = {},
  viewerRoles: IAuthRole[] = getViewerRoles(),
): boolean {
  return getRequestableOrganizationFields(context, viewerRoles).includes(fieldName);
}
